import { Injectable } from '@angular/core';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { catchError, filter, map, mergeMap, Observable, Observer, of, switchMap, tap } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { APPLICATION_REDUCER } from '../reducers/application.reducer';
import { Store } from '@ngrx/store';
import { ScorecardService } from '../../core/services/scorecard.service';
import {
  SCORECARD_CHALLENGE_API_ACTIONS,
  SCORECARD_DETAILS_API_ACTIONS,
  SCORECARD_PREVIEW_API_ACTIONS,
  STATEMENT_CHALLENGE_UPDATE_API_ACTIONS,
  STATEMENT_CREATE_CHALLENGE_API_ACTIONS,
} from '../actions';
import { Message, MessageService } from 'primeng/api';
import { SCORECARD_REDUCER } from '../reducers/scorecard.reducer';
import { IStatement } from '../../shared/models/scorecard-section.model';
import { TScorecardChallengeOperation } from '../../shared/interfaces/challenge.interface';
import {
  APPROVE_CHALLENGE_ERROR,
  APPROVE_CHALLENGE_SUCCESS,
  CREATE_CHALLENGE_ERROR,
  CREATE_CHALLENGE_SUCCESS,
  DELETE_CHALLENGE_ERROR,
  DELETE_CHALLENGE_SUCCESS,
  DENY_CHALLENGE_ERROR,
  DENY_CHALLENGE_SUCCESS,
} from './scorecard-notification.config';
import { IScorecard } from '../../shared/models/scorecard.model';
import { IScorecardPreview } from '../../shared/models/scorecard-preview.model';

@Injectable()
export class ScorecardEffects {
  constructor(
    private readonly scorecardService: ScorecardService,
    private readonly actions$: Actions,
    private readonly store: Store,
    private readonly toastService: MessageService,
  ) {}

  getScorecardPreview$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(SCORECARD_PREVIEW_API_ACTIONS.get),
      concatLatestFrom(() => this.store.select(APPLICATION_REDUCER.selectDataQueryParam)),
      switchMap(([, { queryName, value }]) =>
        this.scorecardService.getScorecardPreview(queryName, value).pipe(
          map((data) => SCORECARD_PREVIEW_API_ACTIONS.success({ data })),
          catchError((error: HttpErrorResponse) => of(SCORECARD_PREVIEW_API_ACTIONS.error({ error }))),
        ),
      ),
    );
  });

  getScorecardDetails$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(SCORECARD_DETAILS_API_ACTIONS.get),
      switchMap(({ scorecardId }) => this.getScorecardPreviewById(scorecardId)),
      concatLatestFrom(() => this.store.select(APPLICATION_REDUCER.selectDataQueryParam)),
      switchMap(([preview, { queryName, value }]) =>
        this.scorecardService.getScorecardDetails(queryName, value, preview).pipe(
          map((data) => SCORECARD_DETAILS_API_ACTIONS.success({ data })),
          catchError((error: HttpErrorResponse) => of(SCORECARD_DETAILS_API_ACTIONS.error({ error }))),
        ),
      ),
    );
  });

  createChallenge$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(STATEMENT_CREATE_CHALLENGE_API_ACTIONS.create),
      concatLatestFrom(() => [
        this.store.select(APPLICATION_REDUCER.selectDataQueryParam),
        this.store.select(SCORECARD_REDUCER.selectScorecardDetails),
      ]),
      mergeMap(([{ data: request }, { queryName, value }, { data: agentPerformanceData }]) =>
        this.scorecardService
          .createStatementChallenge(queryName, value, agentPerformanceData!.scorecardId, request)
          .pipe(
            tap(this.getUpdateChallengeNotificationObserver('create')),
            map((data: IStatement) => STATEMENT_CREATE_CHALLENGE_API_ACTIONS.success({ data })),
            catchError(() => of(STATEMENT_CREATE_CHALLENGE_API_ACTIONS.error({ statementId: request.statementId }))),
          ),
      ),
    );
  });

  updateChallenge$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(STATEMENT_CHALLENGE_UPDATE_API_ACTIONS.update),
      concatLatestFrom(() => [
        this.store.select(APPLICATION_REDUCER.selectDataQueryParam),
        this.store.select(SCORECARD_REDUCER.selectScorecardDetails),
      ]),
      mergeMap(([{ statementId, challengeId, operation, comment }, { queryName, value }, { data }]) =>
        this.scorecardService
          .updateStatementChallenge(queryName, value, data!.scorecardId, statementId, {
            id: challengeId,
            operation,
            comment,
          })
          .pipe(
            tap(this.getUpdateChallengeNotificationObserver(operation)),
            switchMap((data: IStatement) => [
              STATEMENT_CHALLENGE_UPDATE_API_ACTIONS.success({ data, challengeId }),
              SCORECARD_PREVIEW_API_ACTIONS.get(),
            ]),
            catchError(() => of(STATEMENT_CHALLENGE_UPDATE_API_ACTIONS.error({ challengeId }))),
          ),
      ),
    );
  });

  executeScorecardChallenge$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(SCORECARD_CHALLENGE_API_ACTIONS.execute),
      concatLatestFrom(() => [
        this.store.select(APPLICATION_REDUCER.selectDataQueryParam),
        this.store.select(SCORECARD_REDUCER.selectScorecardDetails),
      ]),
      mergeMap(([{ operation, payload }, { value, queryName }, { data }]) =>
        this.scorecardService.executeScorecardChallenge(queryName, value, data!, { operation, payload }).pipe(
          tap(this.getUpdateChallengeNotificationObserver(operation)),
          switchMap((data: IScorecard) => [
            SCORECARD_CHALLENGE_API_ACTIONS.success({ data }),
            SCORECARD_PREVIEW_API_ACTIONS.get(),
          ]),
          catchError(() => of(SCORECARD_CHALLENGE_API_ACTIONS.error())),
        ),
      ),
    );
  });

  private getUpdateChallengeNotificationObserver(operation: TScorecardChallengeOperation): Partial<Observer<any>> {
    let errorConfig: Message, successConfig: Message;

    switch (operation) {
      case 'delete':
        successConfig = DELETE_CHALLENGE_SUCCESS;
        errorConfig = DELETE_CHALLENGE_ERROR;
        break;
      case 'create':
        successConfig = CREATE_CHALLENGE_SUCCESS;
        errorConfig = CREATE_CHALLENGE_ERROR;
        break;
      case 'approve':
        successConfig = APPROVE_CHALLENGE_SUCCESS;
        errorConfig = APPROVE_CHALLENGE_ERROR;
        break;
      case 'deny':
        successConfig = DENY_CHALLENGE_SUCCESS;
        errorConfig = DENY_CHALLENGE_ERROR;
        break;
    }

    return {
      next: () => this.toastService.add(successConfig),
      error: () => this.toastService.add(errorConfig),
    };
  }

  private getScorecardPreviewById(id: string): Observable<IScorecardPreview> {
    return this.store.select(SCORECARD_REDUCER.selectScorecardPreview).pipe(
      filter(({ data }) => Boolean(data)),
      map(({ data }) => data!.find((preview) => preview.scorecardId === Number(id))!),
    );
  }
}
